import {
  filterBySeries,
  SERIES_TITLES,
} from "@bindery/shared/reader/series-filter";
import type { LandingProps, VariantBook } from "@/components/variations/books";
import type { BookmarkValue } from "@/storage/db";
import {
  type DefaultLibraryBook,
  defaultLibraryBooks,
  FEATURED_BOOK_ID,
} from "./default-library";

export const LANDING_PREVIEW_LIMIT = 3;

type Progress = Pick<BookmarkValue, "readingId" | "currentPage" | "completed">;

const isWorksheet = (book: DefaultLibraryBook): boolean =>
  book.title.includes("Sentence Worksheet");

const toVariantBook = (book: DefaultLibraryBook): VariantBook => ({
  readingId: book.reading_id,
  title: book.title,
  byline: book.byline,
  description: book.description,
  imageURL: book.imageURL,
});

function progressSets(bookmarks: readonly Progress[]) {
  const completed = new Set<string>();
  const inProgress = new Set<string>();
  for (const bookmark of bookmarks) {
    if (bookmark.completed) completed.add(bookmark.readingId);
    else if (bookmark.currentPage > 0) inProgress.add(bookmark.readingId);
  }
  return { completed, inProgress };
}

function nextInEachSeries(
  books: DefaultLibraryBook[],
  bookmarks: readonly Progress[],
): DefaultLibraryBook[] {
  const { completed, inProgress } = progressSets(bookmarks);
  return filterBySeries(books, SERIES_TITLES, completed, inProgress);
}

/** The /reader shelf: the catalog minus worksheets, one book per series. */
export function libraryBooks(bookmarks: readonly Progress[]): VariantBook[] {
  return nextInEachSeries(
    defaultLibraryBooks.filter((book) => !isWorksheet(book)),
    bookmarks,
  ).map(toVariantBook);
}

export function landingBooks(bookmarks: readonly Progress[]): LandingProps {
  const featured = defaultLibraryBooks.find(
    (book) => book.reading_id === FEATURED_BOOK_ID,
  );
  if (!featured) throw new Error(`Featured book ${FEATURED_BOOK_ID} missing`);
  return {
    featuredBook: toVariantBook(featured),
    previewBooks: nextInEachSeries(
      defaultLibraryBooks.filter(
        (book) => book.reading_id !== FEATURED_BOOK_ID && !isWorksheet(book),
      ),
      bookmarks,
    )
      .slice(0, LANDING_PREVIEW_LIMIT)
      .map(toVariantBook),
  };
}
